// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import config from 'myWorld/base/config';
import 'myWorld/base/errors';
import 'myWorld/features/feature';

myw.Datasource = myw.MywClass.extend('Datasource', {
    includes: myw.Events,

    defaultFeatureModel: myw.Feature,

    /**
     * @class Abstract datasource class</br>
     * @param  {Database}                database
     * @param  {myWorldDatasourceOptions}    options
     * @constructs
     */
    initialize(database, options) {
        this.setOptions(options);
        this.database = database;
        this.system = database.system;
        this.name = options.name;
        this.type = options.type;
        this.owner = options.owner;

        this.featuresDD = options.featureTypes || {};
        this.appEditableFeatureTypes = _.pick(
            options.featureTypes,
            featureInfo => featureInfo.editable
        );
        this.layerDefs = options.layerDefs || {};
        this.enumerators = {};
        this._completeFeatureTypes = [];
        this._featureModels = {};
        this._ddPromises = {};
        this._state = 'ok';

        _.bindAll(this, '_handleError', '_handleSuccess', 'ensureLoggedIn', '_setupLoginRetry');

        //wrap asynchronous api methods so they all have error and success handling
        myw.Util.wrapMethodsWith(
            this,
            this._apiMethodWrapper.bind(this, true),
            'select',
            'getFeature',
            'getFeatures',
            'getFeaturesAround',
            'countFeatures',
            'runQuery',
            'runSearch',
            'getRelationship',
            'getLayerFeatures',
            'getFeaturesMatching',
            'runTransaction'
        );
        //wrap methods that can cache results with error handling only - we don't want to handle cached results as a success
        myw.Util.wrapMethodsWith(this, this._apiMethodWrapper.bind(this, false), 'getDDInfoFor');

        this.initialized = Promise.resolve(this);
    },

    /**
     * Returns the name of the datasource
     * @return {string}
     */
    getName() {
        return this.options.name;
    },

    /**
     * Returns the external name of the datasource
     * @return {string}
     */
    getExternalName() {
        return (
            this.system.localise(this.options.external_name, this.options.name) +
            (this.options.masterMode ? ' (master)' : '')
        );
    },

    /**
     * Whether this datasource allows editing of features. <br/>
     * Returns false. Override in subclasses
     * @return {Boolean}
     */
    isEditable() {
        return false;
    },

    /**
     * True if this datasource is accessible
     * @param  {string}  [layerName] Name of a layer. If given, current app view mode for the layer will be taken into consideration
     * @return {Boolean}
     */
    isOk(layerName) {
        return this._state == 'ok';
    },

    /**
     * Returns the definition of a given layerName
     * @param  {string} layerName
     * @return {layerDefinition}
     */
    getLayerDef(layerName) {
        return _.findWhere(this.layerDefs, { name: layerName });
    },

    /**
     * Ensures the datasource is logged in
     * Obtains credentials and does login call if appropriate
     * Promise will be rejected if the login fails or the users cancels the login dialog
     * @return {promise} Resolved (to self) when login is complete
     */
    ensureLoggedIn() {
        if (this._loginPromise) return this._loginPromise;

        this._loginPromise = this.initialized
            .then(() => this.getLoginCredentials())
            .then(credentials => {
                if (credentials === true) {
                    //needs credentials given by user
                    return this.loginWithDialog();
                } else if (credentials === false) {
                    //no credentials are necessary
                    return Promise.resolve();
                } else {
                    //use (stored) credentials
                    return this.login(credentials);
                }
            })
            .then(() => this);

        return this._loginPromise.catch(this._handleError).catch(this._setupLoginRetry);
    },

    /**
     * Returns the credentials to use for the login request
     * Returns true if it needs credentials by user
     * Returns false if login is not necessary
     * @return {object|boolean}  Keyed on field name
     */
    getLoginCredentials() {
        if (this.options.username)
            return {
                username: this.options.username,
                password: this.options.password
            };
        else return false;
    },

    /*
     * Setups a retry of ensureLoggedIn when internet becomes available
     */
    _setupLoginRetry(reason) {
        //clear the cached promise otherwise won't retry
        this._loginPromise = undefined;
        //setup retry login when internet becomes available
        this.database.once('internetStatus-changed', e => {
            this.ensureLoggedIn().then(this._handleSuccess);
        });

        return Promise.reject(reason);
    },

    /**
     * Presents a dialog requesting the appropriate login details and then performs the login request
     * Promise is rejected if the user cancels
     * @return {promise}            Resolved when login successful
     */
    loginWithDialog() {
        if (myw.LoginDialog) {
            const loginDialog = new myw.LoginDialog(this);
            return loginDialog.login();
        } else {
            throw new Error('myw.LoginDialog is unavailable');
        }
    },

    /**
     * Obtain DD information for a list of feature types.
     * @param  {string[]}   types       A list of the desired feature types
     * @return {Promise<Object<featureDD>>} Will resolve with an object keyed on feature type
     */
    getDDInfoFor(types) {
        if (!types) {
            // unexpected request, resolve to null to make sure we don't "hide" the error
            return Promise.resolve(null);
        } else {
            return this._ensureDDInfoFor(types);
        }
    },

    /**
     * Obtains information about self's editable features
     * @return {object<featureType,object>} Keyed on feature type
     */
    getEditableFeatureTypes() {
        return _.pick(this.featuresDD, featureDD => featureDD.editable);
    },

    /**
     * Obtains the field name for the primary geometry
     * @param  {string} featureTypeName
     * @return {string}
     */
    getPrimaryGeomFieldNameFor(featureTypeName) {
        const featureDD = this.featuresDD[featureTypeName];
        return featureDD && featureDD.primary_geom_name;
    },

    /**
     * Obtains a feature identified by its urn </br>
     * @param  {string}   urn      Urn of the desired feature
     * @return {Promise<Feature>}  Promise for the specified feature
     */
    getFeatureByUrn(urn, ...args) {
        const parts = urn.split('/'),
            id = parts.splice(-1)[0],
            type = parts.splice(-1)[0];

        if (parts.length > 0 && parts[0] !== this.getName()) {
            throw new Error(
                'Requested feature with a urn of a different datasource',
                urn,
                this.getName()
            );
        }

        //pass on additional arguments
        args = [type, id, ...args];
        return this.getFeature(...args);
    },

    /**
     * Queries the database for features filtered on a field criteria
     * @param  {string}   featureType Name of the table/feature
     * @param  {string}   field     Name of the field to filter on
     * @param  {string}   operator  Operator to use on the filter: "=" or "like"
     * @param  {string}   value     Value to filter on
     * @return {Promise<Array<Feature>>}  Promise for the resulting features
     */
    getFeaturesByValue(featureType, field, operator, value) {
        if (operator == 'equals') operator = '=';

        const params = {
            limit: config['core.queryResultLimit'],
            clauses: [
                {
                    fieldName: field,
                    operator: operator,
                    value: value
                }
            ]
        };
        return this.getFeatures(featureType, params);
    },

    /**
     * Create a new detached feature
     * @param  {string}   featureType     feature type
     */
    createDetachedFeature(featureType) {
        return this._ensureDDInfoFor([featureType]).then(() => {
            const FeatureClass = this._getFeatureClassFor(featureType);
            return new FeatureClass(null, true);
        });
    },

    /**
     * Creates a new detached feature with properties and geometry copied from a given feature
     * @param {Feature} feature
     * @return {Promise<Feature>}
     */
    createDetachedFrom(feature) {
        const FeatureClass = this._getFeatureClassFor(feature.getType());
        const clone = new FeatureClass(null, true);
        const keyFieldName = feature.featureDD.key_name;
        clone.properties = { ...feature.properties };
        delete clone.properties[keyFieldName];
        clone.setGeometry(feature.geometry.type, [...feature.geometry.coordinates]);
        return clone;
    },

    /**
     * Creates a new detached feature with properties and geometry copied from a given featureJson
     * @param  {string}   featureType     Type of feature to create
     * @param {GeoJSON} featureJson     Geojson to copy onto new detached feature
     * @return {Promise<Feature>}
     */
    async createDetachedFromJson(featureType, featureJson) {
        await this._ensureDDInfoFor([featureType]);
        const FeatureClass = this._getFeatureClassFor(featureType);
        const detached = new FeatureClass(null, true);
        const keyFieldName = detached.featureDD.key_name;
        detached.properties = { ...featureJson.properties };
        delete detached.properties[keyFieldName];
        detached.setGeometry(featureJson.geometry.type, [...featureJson.geometry.coordinates]);
        return detached;
    },

    /**
     * Insert a feature to a datasource. <br/>
     * Receives either a detached feature or a feature type and geojson
     * @param  {Feature|string}   detachedFeatureOrFeatureType
     * @param  {featureData}  [insertData]
     * @param  {boolean}   [update=false] If true, an id is provided and feature already exits, update
     *                                    it instead of throwing an error
     * @return {Promise<integer>}    Promise for the id of the inserted feature
     */
    insertFeature(detachedFeatureOrFeatureType, insertData, update = false) {
        const { type, geojson } = this._parseInsertArgs(detachedFeatureOrFeatureType, insertData);
        return this._insertFeature(type, geojson, update);
    },

    /**
     * Insert a detached feature to a datasource. <br/>
     * @param  {Feature}   detachedFeature
     * @param  {boolean}   [update=false] If true, an id is provided and feature already exits, update
     *                                    it instead of throwing an error
     * @return {Promise<Feature>}    Promise for the feature after insertion
     */
    async insertAndGetFeature(detachedFeature, update = false) {
        const key = await this.insertFeature(detachedFeature, undefined, update);
        return this.getFeature(detachedFeature.getType(), key);
    },

    /**
     * Updates a feature in a datasource. <br/>
     * Receives either an existing feature or a feature type, feature id and geojson with the changes to apply
     * @param  {Feature|string}   featureOrType
     * @param  {string}   [featureId]
     * @param  {featureData}   [updateData]
     * @return {Promise<boolean>}    Promise for the success of the operation
     */
    updateFeature(featureOrType, featureId, updateData) {
        let featureType, feature;
        if (typeof featureOrType == 'string') {
            featureType = featureOrType;
        } else {
            //instance of myw.Feature
            feature = featureOrType;
            featureType = feature.type;
            featureId = feature.getId();
            updateData = feature.asGeoJson();
        }
        return this._updateFeature(featureType, featureId, updateData);
    },

    /**
     * Delete a feature<br/>
     * Receives either an existing feature or a feature type and id
     * @param  {Feature|string}   featureOrType
     * @param  {string}   [featureId]
     * @return {Promise}    Promise which will resolve when the operation has completed
     */
    deleteFeature(featureOrType, featureId) {
        let featureType, feature;
        if (typeof featureOrType == 'string') {
            featureType = featureOrType;
        } else {
            //instance of myw.Feature
            feature = featureOrType;
            featureType = feature.type;
            featureId = feature.getId();
        }
        return this._deleteFeature(featureType, featureId);
    },

    /**
     * Returns the networks a given feature can be part of
     * @param  {MyWorldFeature} feature
     * @return {promise}         Network properties keyed on network name
     */
    getNetworksFor(feature) {
        return Promise.resolve({});
    },

    /**
     * Sends a request via the myWorld server<br/>
     * Uses the url specified in the datasource definition
     * @param  {Object}     requestParams   Key/value pairs that will be passed on as url parameters
     * @param  {string}     [options.urlFieldName='url']    Name of property in datasource's spec that holds the base url for the request
     * @param  {string}     [options.relativeUrl='']        Relative url to append to the base url
     * @return {Promise<Object>}  Promise for the results (parsed from json)
     */
    tunnelRequest(requestParams, options) {
        return this.system.tunnelDatasourceRequest(this.getName(), requestParams, options);
    },

    /*
     * Returns a url for tunneled requests
     * @param  {object}     requestParams   Parameters to send to the external selection server
     * @param  {string}     options.featureType
     * @param  {string}     options.urlFieldName        Name of datasource field that holds the base url
     * @param  {string}     [options.relativeUrl='']
     * @return {String}     url for the request
     */
    buildTunnelRequestUrl(requestParams, options) {
        return this.system.buildTunnelDatasourceRequestUrl(this.getName(), requestParams, options);
    },

    /**
     * Wraps a datasource api method with error handling
     * @param  {boolean}  handleSuccess     Whether resolved promises should be handled as success or not
     * @param  {function} originalMethod    Original method to be wrapped
     * @return {function} New method with error/success handling
     * @private
     */
    _apiMethodWrapper(handleSuccess, originalMethod) {
        return (...args) => {
            if (this.initialized.isFulfilled()) {
                const originalPromise = Promise.resolve(originalMethod.apply(this, args));
                if (handleSuccess) {
                    return originalPromise.then(this._handleSuccess, this._handleError);
                } else {
                    return originalPromise.catch(this._handleError);
                }
            } else {
                const reason = this.initialized.isRejected() && this.initialized.reason();
                if (reason) {
                    return Promise.reject(
                        `Datasource '${this.options.name}' has not been successfully initialized: `,
                        reason
                    );
                } else {
                    return Promise.reject(
                        `Datasource '${this.options.name}' is still initializing. Make sure your call is preceded by waiting for .initialized to resolve`
                    );
                }
            }
        };
    },

    /**
     * Informs the database via a changed event that the datasource is "ok"
     * Call after a successful request
     * @protected
     */
    _handleSuccess(result) {
        if (this._state !== 'ok') {
            this.fire('changed', { state: 'ok', datasource: this });
            myw.trace('datasource', 2, `Datasource '${this.getName()}' state changed to ok`);
        }
        this._state = 'ok';

        return result;
    },

    /**
     * Handles an error with accessing services by firing the changed event
     * @protected
     */
    _handleError(reason) {
        const expectedErrors = ['ObjectNotFoundError', 'UnauthorizedError'];
        const errorName = (reason && reason.name) || reason;
        if (!_(expectedErrors).includes(errorName)) {
            this.fire('changed', { state: 'error', reason: reason, datasource: this });
            this._state = 'error';
            myw.trace(
                'datasource',
                2,
                `Datasource '${this.getName()}' state changed to error: ${reason}`
            );
            myw.trace('datasource', 4, reason.stack);
        }

        return Promise.reject(reason);
    },

    /**
     * Returns a list of myWorldFeatures from the provided list of json features <br/>
     * _ensureDDInfoFor() should be called beforehand
     * @param  {featureData}    featureData         Objects obtained from parsing json results
     * @param  {string}         featureType         Type of feature
     * @param  {object}         options             Options to be passed on to constructor
     * @protected
     */
    _asFeature(featureData, featureType, options) {
        const featureClass = this._getFeatureClassFor(featureType);

        return new featureClass(featureData, options);
    },

    /**
     * Obtains the JavaScript class to instantiate a feature of the given type <br/>
     * _ensureDDInfoFor() should be called beforehand
     * @param  {string}     [featureType]     Type of feature
     * @return {Class}
     * @protected
     */
    _getFeatureClassFor(featureType) {
        const dsName = this.getName();
        const key = dsName == 'myworld' ? featureType : `${dsName}/${featureType}`;
        let featureModel = this._featureModels[key];
        let ParentClass;
        let featureDD;

        if (!featureModel) {
            //no cached feature model for this feature type yet, create it

            featureDD = this.featuresDD[featureType];
            if (featureDD) ParentClass = myw.featureModels[key] || this.defaultFeatureModel;
            else {
                //use top level class to allow usage with out-of-date DD information
                ParentClass = myw.Feature;
                console.log(
                    `Warning: missing DD information for ${key} in datasource ${dsName}. Update DD info?`
                );
            }

            featureModel = ParentClass.extend(key, {
                datasource: this,
                database: this.database,
                type: featureType,
                featureDD: featureDD
            });
            this._featureModels[key] = featureModel;
        }
        return featureModel;
    },

    /**
     * Returns the appropriate JS class to instante a layer
     * @param  {layerDefinition}    layerDef        Layer definition. Property 'jsClass' will be used to look for the desired class
     * @param  {function}           defaultClass    Class to use if
     * @return {function}              Class constructor
     * @protected
     */
    _getLayerClassFor(layerDef, defaultClass) {
        const Layer = myw.Util.evalAccessors(layerDef.jsClass) || defaultClass;

        if (typeof Layer !== 'function') {
            throw new Error(
                `Definition for ${layerDef.name} doesn't evalute to a class. Expression: ${layerDef.jsClass}`
            );
        }

        return Layer;
    },

    /**
     * Ensures DD information is available for given feature types.
     * Request information (by calling _fetchDDInfoForTypes()) only if the information is not cached and has not already been requested
     * @param  {Array<string>} types An array with feature types
     * @return {Promise<featureDD>} Resolved when the information for all types is available
     * @private
     */
    _ensureDDInfoFor(types) {
        //ENH: provide a method to invalidate the cache (useful for native app)
        const featuresDD = this.featuresDD;
        const promises = this._ddPromises;
        const allTypesRequested = _.keys(promises);
        const typesToRequest = _.difference(types, allTypesRequested);
        const typesOnTheWay = _.intersection(types, allTypesRequested);
        let promisesTypesOnTheWay = _.values(_.pick(promises, typesOnTheWay));

        if (typesToRequest.length > 0) {
            const promise = this._getDDInfoForTypes(typesToRequest);
            _.each(typesToRequest, type => {
                promises[type] = promise;
            });
            promisesTypesOnTheWay = promisesTypesOnTheWay.concat([promise]);
        }

        return Promise.all(promisesTypesOnTheWay).then(() => {
            const missingTypes = _.difference(types, _.keys(featuresDD));
            if (missingTypes.length > 0) {
                throw new myw.MissingFeatureDD(`Missing DD information for types:${missingTypes}`);
            }

            return featuresDD;
        });
    },

    /**
     * Obtains and processes DD information for the given feature types. <br/>
     * Obtains the DD information from the myWorld system. Can be overriden in subclasses.
     * @param  {string[]} types Feature types
     * @return {Promise} Promise which will resolve when the DD information has been processed
     * @protected
     */
    _getDDInfoForTypes(types) {
        // request from the server the DD information
        return this._fetchDDInfoForTypes(types).then(data => {
            // add the received DD information to the dd property
            _.each(data.features_dd, (featureDD, key) => {
                this._completeFeatureTypes.push(key);
                if (key in this.featuresDD) _.extend(this.featuresDD[key], featureDD);
                else this.featuresDD[key] = featureDD; //ENH: consider including all accessible features in startup info (not just application accessible)
            });
            _.extend(this.enumerators, data.enumerators);

            const localise = this.system.localise.bind(this.system);

            //Go over the new features to setup additional properties:
            //- setup a defaults object
            //- link enumerated fields with the enumerator definition
            for (const featureType in data.features_dd) {
                const featureDD = this.featuresDD[featureType];
                if (!featureDD) continue;
                featureDD.defaults = {};
                if (featureDD.field_groups.length) {
                    featureDD.field_groups = featureDD.field_groups.map(group => {
                        const missing_language_text = `${featureType}.groups[${group['position']}]`;
                        group['display_name'] = localise(group.display_name, missing_language_text);
                        return group;
                    });
                }
                for (const fieldName in featureDD.fields) {
                    const fieldDD = featureDD.fields[fieldName];
                    if (fieldDD.hasOwnProperty('default')) {
                        if (fieldDD.type == 'boolean') {
                            featureDD.defaults[fieldName] =
                                fieldDD['default'].toUpperCase() == 'TRUE';
                        } else {
                            featureDD.defaults[fieldName] = fieldDD['default'];
                        }
                    }
                    if (fieldDD['enum']) {
                        const enumerator = data.enumerators[fieldDD['enum']];
                        if (enumerator) {
                            const enumValues = enumerator.values.map(val => {
                                val.display_value = localise(val.display_value, val.value);
                                return val;
                            });
                            fieldDD.enumValues = enumValues;
                        } else
                            console.log(
                                `Error. Field ${featureDD.name}.${
                                    fieldDD.internal_name
                                } references missing enumerator: "${fieldDD['enum']}"`
                            );
                    }
                    fieldDD.external_name = localise(fieldDD.external_name, fieldDD.internal_name);
                }
            }

            return this.featuresDD;
        });
    },

    /**
     * Fetches DD information for the given feature types
     * @private
     * @param  {string[]} types Feature types
     * @return {Promise} Promise which will resolve when the DD information is available
     */
    _fetchDDInfoForTypes(types) {
        // request from the server the DD information
        return this.system.server.getDDInfoForTypes(this.name, types);
    },

    /**
     * Insert a feature to the datasource
     * @param  {Feature|string}   detachedFeatureOrFeatureType
     * @param  {featureData}  [insertData]
     * @return {Promise<integer>}    Promise for the id of the inserted feature
     * @protected
     */
    _insertFeature(detachedFeatureOrFeatureType, insertData) {
        throw new Error(`Missing implementation of _insertFeature() for ${this.getName()}`);
    },

    /**
     * Update a feature in the datasource
     * @param  {string}   featureOrType
     * @param  {string}   [featureId]
     * @param  {featureData}   [updateData]
     * @return {Promise<boolean>}    Promise for the success of the operation
     * @protected
     */
    _updateFeature(featureOrType, featureId, updateData) {
        throw new Error(`Missing implementation of _updateFeature() for ${this.getName()}`);
    },

    /**
     * Delete a feature from the datasource
     * @param  {string}   featureOrType
     * @param  {string}   [featureId]
     * @return {Promise}    Promise which will resolve when the operation has completed
     * @protected
     */
    _deleteFeature(featureOrType, featureId) {
        throw new Error(`Missing implementation of _deleteFeature() for ${this.getName()}`);
    },

    /**
     * Returns autocomplete suggestions for executing an "in window" query
     * @param  {string}            searchTerm   Text to search for
     * @param  {array<featureDD>}  featureTypes Feature types to get queries for
     * @return {Array<autoCompleteResult>}
     * @protected
     */
    _querySuggestions(searchTerm, featureTypes) {
        const suggestions = [];

        _.each(featureTypes, (featureDD, featureTypeName) => {
            _.each(featureDD.queries, queryDef => {
                if (queryDef.lang && queryDef.lang !== myw.localisation.languageAsPerSetting)
                    return;

                const extraTerms = myw.Util.termsMatch(searchTerm, queryDef.matched_value);
                if (extraTerms !== '') return; //false -> didn't match, truthy -> remaining terms

                //all terms matched the query, return suggestion(s) for this query definition
                const suggestion = this._querySuggestion.bind(this, featureTypeName, queryDef);
                if (this.options.fullQuery) suggestions.push(suggestion());
                if (this.options.inWindowQuery) suggestions.push(suggestion('window'));
                if (this.options.inSelectionQuery) suggestions.push(suggestion('selection'));
            });
        });

        return suggestions;
    },

    /**
     * Returns a new query suggestion
     * @return {object}
     * @protected
     */
    _querySuggestion(featureTypeName, queryDef, spatialRestriction) {
        const suggestion = {
            label: queryDef.display_value,
            value: queryDef.matched_value,
            type: 'query',
            data: {
                feature_type: featureTypeName,
                id: queryDef.display_value, //used in search control to group queries
                spatial_restriction: spatialRestriction,
                has_geometry: true
            }
        };
        if (queryDef.attrib_query) {
            suggestion.data.clauses = this._parseAttributeQuery(queryDef.attrib_query);
        }
        return suggestion;
    },

    /**
     * For a given search text, obtains the matched featureTypes and remaining (umatched) terms
     * @param  {string}         searchTerm   Search terms (typed by a user)
     * @param  {featuresDD}  featureTypes Feature types to search on
     * @return {searchDetails}
     * @private
     */
    _getSearchDetailsFor(searchTerm, featureTypes) {
        const result = {};

        _.each(
            featureTypes,
            (featureDD, featureType) => {
                const idTerms = featureDD.search_id_terms;
                if (!idTerms) return;

                const extraTerms = myw.Util.termsMatch(searchTerm, idTerms.join(' '));

                if (extraTerms === false) {
                    //no matches
                } else {
                    result[featureType] = { dd: featureDD, extraTerms: extraTerms };
                }
            },
            this
        );

        return result;
    },

    /**
     * Parse the attribute query string
     * @private
     * @param  {string} attributeQuery  The attribute query. Example: [urgent]=true & [problem_type]='Wires Down'
     * @return {Array<clause>}  An array of clause objects
     */
    _parseAttributeQuery(parseAttributeQuery) {
        const clauseStrings = parseAttributeQuery.split('&');
        const clauses = _.map(clauseStrings, clauseString =>
            this._parseAttributeQueryClause(clauseString)
        );

        //exclude null clauses before returning
        return _.reject(clauses, clause => !clause);
    },

    /**
     * List of regular expressions to use when matching query attribute clauses with post processing if required
     * @private
     */
    _attributeQueryMatchers: [
        {
            // null value
            regexp: /\[(.+)]\s*(=|<>)\s*null/
        },
        {
            // String
            regexp: /\[(.+)]\s*(=|<>|like|ilike)\s*'(.+)'/
        },
        {
            // Boolean
            regexp: /\[(.+)]\s*(=|<>)\s*(true|false)/,
            postProcess(clause) {
                // Convert value from string to boolean
                clause.value = clause.value == 'true';
            }
        },
        {
            // Variable
            regexp: /\[(.+)]\s*(=|<>|>=|<=|>|<)\s{(.+)}/
        },
        {
            // Number
            regexp: /\[(.+)]\s*(=|<>|>=|<=|>|<)\s*(.+)/,
            postProcess(clause) {
                // Convert value from string to number
                clause.value = parseFloat(clause.value);
            }
        }
    ],

    /**
     * Parse a single attribute query clause
     * @private
     * @param  {string} attributeClause  The attribute query clause. Example: [problem_type]='Wires Down'

     * @return {queryClause}  A clause object (or nothing if the clause is invalid)
     */
    _parseAttributeQueryClause(attributeClause) {
        if (!attributeClause) return; // Empty string, don't return a clause

        for (const matcherIndex in this._attributeQueryMatchers) {
            const matcher = this._attributeQueryMatchers[matcherIndex],
                clause = this._matchAttributeQueryClause(matcher, attributeClause);

            if (clause) return clause;
        }

        // No match found, don't return a clause
        console.log(`Error processing query clause: ${attributeClause}`);
    },

    /**
     * See if the clause string matches the form expected by the matcher. If so
     * return the corresponding clause object
     * @private
     * @param  {string} attributeClause  The attribute query clause string
     * @return {Clause}  A clause object (or null if the clause string does not match)
     */
    _matchAttributeQueryClause(matcher, attributeClause) {
        let clause;
        let value;
        const matches = matcher.regexp.exec(attributeClause);

        if (matches) {
            value = matches.length > 3 ? matches[3] : null; //handle null value case

            clause = {
                fieldName: matches[1],
                operator: matches[2],
                value: value
            };

            if ('postProcess' in matcher) {
                matcher.postProcess(clause);
            }
        }
        return clause;
    },

    /*
     * Processes arguments to insertFeature,
     * which receives either a detached feature or a feature type and geojson
     * Returns an object of the format: {type, geojson}
     * @param {*} detachedFeatureOrFeatureType
     * @param {*} insertData
     */
    _parseInsertArgs(detachedFeatureOrFeatureType, insertData) {
        if (typeof detachedFeatureOrFeatureType == 'string') {
            return { type: detachedFeatureOrFeatureType, geojson: insertData };
        }
        //first arg is instance of myw.Feature
        const feature = detachedFeatureOrFeatureType;
        return { type: feature.type, geojson: feature.asGeoJson() };
    }
});

export default myw.Datasource;
